'use client';

import { type ReactNode, useMemo, useRef } from 'react';
import {
  AssistantRuntimeProvider,
  type ChatModelAdapter,
  useLocalRuntime,
} from '@assistant-ui/react';

import { getStaticConfig } from ':support/config';
import { logError } from ':support/utils/error-vitals/logging';
import { setRedirectUri } from ':support/utils/session-utils/redirectUriStorage';

type AIStreamToken = {
  token?: string;
  done?: boolean;
  thread_id?: string;
  error?: string;
};

const AI_AGENT_ID = 'support_hub_assistant';
export const STREAM_TIMEOUT_MS = 120_000;

function generateThreadId() {
  return `thread-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Streaming Chat Model Adapter
 *
 * Uses Server-Sent Events (SSE) to stream tokens from the sfdc-integration-service.
 * Authentication is handled by Entry Gateway via session cookies — edge-auth headers
 * are injected server-side, not by the client.
 */
export function createStreamingAdapter(
  threadIdRef: React.MutableRefObject<string>,
): ChatModelAdapter {
  return {
    async *run({ messages, abortSignal }) {
      const lastMessageIndex = messages.length - 1;

      const messageText = messages[lastMessageIndex].content
        .filter((part): part is { type: 'text'; text: string } => part.type === 'text')
        .map((part) => part.text)
        .join('\n');

      // eslint-disable-next-line compat/compat -- AbortController is required for timeout management
      const timeoutController = new AbortController();
      let activeTimeoutId = setTimeout(() => timeoutController.abort(), STREAM_TIMEOUT_MS);
      // eslint-disable-next-line compat/compat -- AbortSignal.any is required to combine user and timeout signals
      const combinedSignal = AbortSignal.any([abortSignal, timeoutController.signal]);

      const response = await fetch('/service-api/StreamAIChat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
        },
        credentials: 'include',
        body: JSON.stringify({
          agent_id: AI_AGENT_ID,
          message: messageText,
          thread_id: threadIdRef.current,
        }),
        signal: combinedSignal,
      });

      if (response.status === 401) {
        clearTimeout(activeTimeoutId);
        const { logoutURL } = getStaticConfig();
        if (typeof window !== 'undefined' && window.location.pathname !== '/') {
          setRedirectUri(window.location.pathname + window.location.search);
        }
        if (typeof window !== 'undefined') {
          window.location.assign(logoutURL);
        }
        throw new Error('Unauthorized');
      }

      if (!response.ok) {
        clearTimeout(activeTimeoutId);
        const errorMessage = `AI chat request failed with status ${response.status}`;
        logError(new Error(errorMessage), {
          context: 'ai-model-provider',
          severity: 'error',
        });
        throw new Error(errorMessage);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        clearTimeout(activeTimeoutId);
        const error = new Error('No response body available for streaming');
        logError(error, {
          context: 'ai-model-provider',
          severity: 'error',
        });
        throw error;
      }

      const resetTimeout = () => {
        clearTimeout(activeTimeoutId);
        activeTimeoutId = setTimeout(() => timeoutController.abort(), STREAM_TIMEOUT_MS);
      };

      const decoder = new TextDecoder();
      let accumulatedText = '';
      let lineBuffer = '';

      try {
        while (true) {
          // eslint-disable-next-line no-await-in-loop -- sequential stream reads are intentional
          const { done, value } = await reader.read();
          if (done) break;

          resetTimeout();

          lineBuffer += decoder.decode(value, { stream: true });
          const parts = lineBuffer.split('\n');
          lineBuffer = parts.pop() ?? '';
          const lines = parts;

          for (const line of lines) {
            if (!line.startsWith('data: ')) {
              // eslint-disable-next-line no-continue -- continue is expected for SSE line filtering
              continue;
            }

            const jsonStr = line.slice(6).trim();
            if (!jsonStr) {
              // eslint-disable-next-line no-continue -- continue is expected for skipping empty data fields
              continue;
            }

            try {
              const data = JSON.parse(jsonStr) as AIStreamToken;

              if (data.error) {
                logError(new Error(data.error), {
                  context: 'ai-model-provider',
                  severity: 'error',
                });
                throw new Error(data.error);
              }

              if (data.done) {
                if (data.thread_id) {
                  // eslint-disable-next-line no-param-reassign -- ref mutation is the intended API
                  threadIdRef.current = data.thread_id;
                }
                return;
              }

              if (data.token) {
                accumulatedText += data.token;
                yield {
                  content: [{ type: 'text' as const, text: accumulatedText }],
                };
              }
            } catch (parseError) {
              if (!(parseError instanceof SyntaxError)) {
                throw parseError;
              }
            }
          }
        }
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') {
          if (timeoutController.signal.aborted && !abortSignal.aborted) {
            logError(new Error('AI chat stream timed out due to inactivity'), {
              context: 'ai-model-provider',
              severity: 'error',
            });
            throw new Error('The response timed out. Please try again.');
          }
          return;
        }
        throw err;
      } finally {
        clearTimeout(activeTimeoutId);
        reader.releaseLock();
      }
    },
  };
}

export function AIModelProvider({
  children,
}: Readonly<{
  children: ReactNode;
}>) {
  const threadIdRef = useRef<string>(generateThreadId());
  const adapter = useMemo(() => createStreamingAdapter(threadIdRef), []);
  const runtime = useLocalRuntime(adapter);

  return <AssistantRuntimeProvider runtime={runtime}>{children}</AssistantRuntimeProvider>;
}
